import React, { useEffect, useMemo, useRef, useState } from 'react';

type WebProject = {
  title: string;
  url: string;
  image: string;
};

const WEB_PROJECTS: WebProject[] = [
  {
    title: 'Elen Kejdova - Professional Portfolio',
    url: 'https://elenkey.cz/',
    image: '/assets/elenkey-preview.png',
  },
  {
    title: 'Tiimiakatemia Prague - Organization Website',
    url: 'https://ip.pef.czu.cz/',
    image: '/assets/tiimi-preview.png',
  },
  {
    title: 'VR Edu Klub - License Management Portal',
    url: 'https://vreduklub.cz/Prihlaseni?ReturnUrl=%2F',
    image: '/image.png',
  },
];

const WebDeveloperShowcase: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const dragStartXRef = useRef<number | null>(null);
  const dragDeltaXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const total = WEB_PROJECTS.length;
  const visibleItems = isDesktop ? 2.5 : 1;
  const edgeCloneCount = Math.max(1, Math.ceil(visibleItems));
  const startIndex = edgeCloneCount;

  const carouselProjects = useMemo(() => {
    if (total <= 1) return WEB_PROJECTS;
    return [
      ...WEB_PROJECTS.slice(-edgeCloneCount),
      ...WEB_PROJECTS,
      ...WEB_PROJECTS.slice(0, edgeCloneCount),
    ];
  }, [edgeCloneCount, total]);

  const realIndex = useMemo(() => {
    if (total <= 1) return 0;
    return (activeIndex - startIndex + total) % total;
  }, [activeIndex, startIndex, total]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');

    const applyViewport = () => {
      setIsDesktop(mediaQuery.matches);
    };

    applyViewport();
    mediaQuery.addEventListener('change', applyViewport);

    return () => {
      mediaQuery.removeEventListener('change', applyViewport);
    };
  }, []);

  useEffect(() => {
    setActiveIndex(total > 1 ? startIndex : 0);
  }, [startIndex, total]);

  const trackStyle = useMemo(
    () => ({
      transform: `translateX(-${activeIndex * (100 / visibleItems)}%)`,
      transition: isTransitionEnabled ? 'transform 500ms ease-out' : 'none',
    }),
    [activeIndex, isTransitionEnabled, visibleItems]
  );

  const goNext = () => {
    setActiveIndex((prev) => prev + 1);
  };

  const goPrev = () => {
    setActiveIndex((prev) => prev - 1);
  };

  const handleTransitionEnd = () => {
    if (total <= 1) return;

    if (activeIndex < startIndex) {
      setIsTransitionEnabled(false);
      setActiveIndex((prev) => prev + total);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitionEnabled(true);
        });
      });
    } else if (activeIndex >= startIndex + total) {
      setIsTransitionEnabled(false);
      setActiveIndex((prev) => prev - total);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitionEnabled(true);
        });
      });
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = event.clientX;
    dragDeltaXRef.current = 0;
    isDraggingRef.current = false;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartXRef.current === null) return;
    dragDeltaXRef.current = event.clientX - dragStartXRef.current;
    if (Math.abs(dragDeltaXRef.current) > 8) {
      isDraggingRef.current = true;
    }
  };

  const handlePointerUp = () => {
    if (dragStartXRef.current === null) return;

    const threshold = 60;
    if (dragDeltaXRef.current <= -threshold) {
      goNext();
      suppressClickRef.current = true;
    } else if (dragDeltaXRef.current >= threshold) {
      goPrev();
      suppressClickRef.current = true;
    }

    dragStartXRef.current = null;
    dragDeltaXRef.current = 0;
    isDraggingRef.current = false;
  };

  const handleCardClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDraggingRef.current || suppressClickRef.current) {
      event.preventDefault();
      suppressClickRef.current = false;
    }
  };

  return (
    <section id="web-dev" className="py-12 md:py-20 bg-background">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-4xl md:text-6xl font-bold mb-4 md:mb-6">WEB DEVELOPER</h2>
        <p className="brutalist-text text-sm md:text-base mb-8 md:mb-12 max-w-3xl">
          Showcase of finished websites I designed and delivered for real clients and organizations.
        </p>

        <div className="relative left-1/2 w-screen -translate-x-1/2 px-4 md:px-8">
          <div className="overflow-hidden">
            <div
              className="flex"
              style={trackStyle}
              aria-live="polite"
              onTransitionEnd={handleTransitionEnd}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              {carouselProjects.map((project, index) => (
                <a
                  key={`${project.url}-${index}`}
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full md:w-[40%] shrink-0 px-2 md:px-3 select-none touch-pan-y ${
                    index % 2 === 0 ? 'md:rotate-1' : 'md:-rotate-1'
                  }`}
                  aria-label={`Open ${project.title}`}
                  onClick={handleCardClick}
                  draggable={false}
                >
                  <div className="brutalist-container block transition-transform hover:rotate-0">
                    <div className="aspect-video bg-surface-alt mb-4 overflow-hidden border-2 border-surface-alt">
                      <img
                        src={project.image}
                        alt={`${project.title} preview`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        draggable={false}
                      />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold">{project.title}</h3>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 gap-4">
            <button
              type="button"
              onClick={goPrev}
              className="brutalist-button px-4 py-2"
              aria-label="Previous project"
            >
              {'<'}
            </button>

            <div className="flex items-center gap-2" aria-label="Project slide indicators">
              {WEB_PROJECTS.map((project, index) => (
                <button
                  key={project.url}
                  type="button"
                  onClick={() => setActiveIndex(index + startIndex)}
                  className={`h-3 w-3 border-2 border-text transition-colors ${
                    index === realIndex ? 'bg-text' : 'bg-transparent'
                  }`}
                  aria-label={`Go to project ${index + 1}`}
                  aria-current={index === realIndex}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              className="brutalist-button px-4 py-2"
              aria-label="Next project"
            >
              {'>'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebDeveloperShowcase;
